import * as Sentry from '@sentry/react'
import 'bootstrap/dist/css/bootstrap.min.css'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, Navigate, useLocation, useNavigationType } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { ErrorPage } from './error-page'
import './main.css'
import { Root } from './root'
import { RouteKey, Routes } from './routes'
import { AnalysisDetailsPage } from './routes/analysis-details-page'
import { LoginPage } from './routes/login-page'
import { RepositoriesPage } from './routes/repositories-page'
import { ScansOverviewPage } from './routes/scans-overview-page'
import { WithLayout, WithStubLayout } from './utils/higher-order-components/with-layout'

import.meta.env.PROD &&
  !import.meta.env.MOCKS &&
  Sentry.init({
    dsn: 'https://3e148ebf8aefc7d69909903b8ada4f76@o4505326209073152.ingest.us.sentry.io/4505940512342016',
    environment: window.location.host,
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [/\/api\//],
  })

async function enableMocking() {
  if (!import.meta.env.MOCKS) return

  document.cookie = `q_session=123; Path=/; Max-Age=${60 * 60 * 24 * 14}`
  console.log('Set mock auth cookie')

  console.log('Enabling mocks')
  const worker = await Promise.all([import('msw/browser'), import('../playwright/handlers')]).then(([{ setupWorker }, { handlers }]) => {
    console.log('Setting up worker')
    return setupWorker(...handlers)
  })

  console.log('Starting worker')
  return worker.start({ onUnhandledRequest: 'bypass' })
}

enableMocking().then(() => {
  const container = document.getElementById('root')
  invariant(container)
  const root = createRoot(container)

  const router = createBrowserRouter([
    {
      path: '/',
      element: <Root />,
      errorElement: <ErrorPage />,
      children: Object.keys(Routes).map(route =>
        match<RouteKey>(route as RouteKey)
          .with('LoginPage', key => ({
            path: Routes[key].path,
            element: (
              <WithStubLayout>
                <LoginPage />
              </WithStubLayout>
            ),
          }))
          .with('IntroPage', key => ({
            path: Routes[key].path,
            element: <Navigate to={Routes.RepositoriesPage.path} replace />,
          }))
          .with('WelcomePage', key => ({
            path: Routes[key].path,
            element: <Navigate to={Routes.RepositoriesPage.path} replace />,
          }))
          .with('HomePage', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <RepositoriesPage />
              </WithLayout>
            ),
          }))
          .with('RepositoriesPage', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <RepositoriesPage />
              </WithLayout>
            ),
          }))
          .with('AnalysisDetailsPage', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <AnalysisDetailsPage />
              </WithLayout>
            ),
          }))
          .with('AnalysisDetailsPageDeprecated', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <AnalysisDetailsPage />
              </WithLayout>
            ),
          }))
          .with('AnalysisDetailsPageFindingFixDrawerDeprecated', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <AnalysisDetailsPage />
              </WithLayout>
            ),
          }))
          .with('AnalysisDetailsPageFindingFixDrawer', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <AnalysisDetailsPage />
              </WithLayout>
            ),
          }))
          .with('AnalysisDetailsPageFindingTriageDrawer', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <AnalysisDetailsPage />
              </WithLayout>
            ),
          }))
          .with('ScansOverviewPage', key => ({
            path: Routes[key].path,
            element: (
              <WithLayout>
                <ScansOverviewPage />
              </WithLayout>
            ),
          }))
          .exhaustive()
      ),
    },
  ])

  root.render(
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  )
})
